import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Monitor, Moon, Sun } from "lucide-react";
import {
  appearanceSettingsSchema,
  defaultModuleTheme,
  moduleThemeCss,
  resolveModuleTheme,
  type AppearanceSettings as Appearance,
  type ModuleTheme,
  type Repository,
  type TenantSettings,
} from "@proof/core";
import { Select, Button, Control } from "./components";

export type DisplayMode = "system" | "light" | "dark";
const modeKey = "proof-studio-display-mode";
const readMode = (): DisplayMode | undefined => {
  try {
    const cookie = document.cookie
      .split("; ")
      .find((part) => part.startsWith(`${modeKey}=`))
      ?.slice(modeKey.length + 1);
    const value = cookie || localStorage.getItem(modeKey);
    return value === "light" || value === "dark" || value === "system"
      ? value
      : undefined;
  } catch {
    return undefined;
  }
};
const saveModeCookie = (mode: DisplayMode) => {
  try {
    document.cookie = `${modeKey}=${mode}; Path=/; Max-Age=31536000; SameSite=Lax${location.protocol === "https:" ? "; Secure" : ""}`;
  } catch {
    // Storage restrictions must not prevent changing the display mode.
  }
};
const listeners = new Set<() => void>();
/** The viewer's display mode; falls back to the workspace default. */
export function useDisplayMode(fallback: DisplayMode = "system") {
  const [mode, setModeState] = useState<DisplayMode>(
    () => readMode() || fallback,
  );
  useEffect(() => {
    const sync = () => setModeState(readMode() || fallback);
    listeners.add(sync);
    sync();
    return () => void listeners.delete(sync);
  }, [fallback]);
  useEffect(() => {
    if (readMode()) saveModeCookie(mode);
    const media = matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      document.documentElement.dataset.theme =
        mode === "system" ? (media.matches ? "dark" : "light") : mode;
    };
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [mode]);
  const setMode = (next: DisplayMode) => {
    try {
      saveModeCookie(next);
      localStorage.setItem(modeKey, next);
    } catch {
      // Private windows keep the choice for this page only.
    }
    setModeState(next);
    listeners.forEach((listener) => listener());
  };
  return [mode, setMode] as const;
}
/** System / Light / Dark switch for the sidebar. */
export function DisplayModeToggle({
  fallback = "system",
}: {
  fallback?: DisplayMode;
}) {
  const [mode, setMode] = useDisplayMode(fallback);
  const options: [DisplayMode, string, typeof Sun][] = [
    ["system", "System", Monitor],
    ["light", "Light", Sun],
    ["dark", "Dark", Moon],
  ];
  return (
    <div className="display-mode" role="radiogroup" aria-label="Display mode">
      {options.map(([value, label, Icon]) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={mode === value}
          title={`${label} display mode`}
          className={mode === value ? "active" : ""}
          onClick={() => setMode(value)}
        >
          <Icon size={13} aria-hidden="true" />
          <span>{label}</span>
        </button>
      ))}
    </div>
  );
}
/** Applies the resolved site + company module theme to the studio. */
export function ModuleThemeStyle({ appearance }: { appearance?: Appearance }) {
  const css = useMemo(() => {
    try {
      return moduleThemeCss(resolveModuleTheme(appearance), ".app-shell");
    } catch {
      return "";
    }
  }, [appearance]);
  return css ? <style data-module-theme="">{css}</style> : null;
}

type Level = "site" | "company";
const colorTokens: [keyof ModuleTheme, string][] = [
  ["primary", "Primary (buttons, highlights)"],
  ["accent", "Accent (links)"],
  ["ink", "Text"],
  ["muted", "Secondary text"],
  ["background", "Page background"],
  ["surface", "Panels & cards"],
  ["border", "Borders"],
];
const presets: { name: string; theme: ModuleTheme }[] = [
  { name: "Studio green", theme: {} },
  {
    name: "Ocean",
    theme: {
      primary: "#1f5f8b",
      accent: "#2b7bb9",
      ink: "#1d2b36",
      background: "#f4f7fa",
      border: "#dde5ec",
    },
  },
  {
    name: "Plum",
    theme: {
      primary: "#6b3f7a",
      accent: "#9b4d96",
      ink: "#2c2230",
      background: "#f8f6f9",
      border: "#e7e0ea",
    },
  },
  {
    name: "Graphite",
    theme: {
      primary: "#2f3437",
      accent: "#4a6fa5",
      ink: "#1f2326",
      background: "#f5f5f5",
      border: "#e2e2e2",
      radius: 4,
    },
  },
];

/**
 * GUI style configurator for the plug-in modules. The site level sets the
 * defaults for every company; the company level uses the site setting for
 * each token unless you override it. Advanced controls stay folded away.
 */
export function AppearanceSettings({
  repository,
  tenantId,
  canEditSite = true,
  notify,
  onSaved,
}: {
  repository: Repository;
  tenantId: string;
  canEditSite?: boolean;
  notify(message: string): void;
  onSaved?(appearance: Appearance): void;
}) {
  const [level, setLevel] = useState<Level>("company"),
    [draft, setDraft] = useState<Appearance>({ site: {}, company: {} }),
    [revision, setRevision] = useState(0),
    [settings, setSettings] = useState<TenantSettings>(),
    [busy, setBusy] = useState(false),
    [dirty, setDirty] = useState(false);
  useEffect(() => {
    let active = true;
    void repository
      .tenantSettings(tenantId)
      .then((value) => {
        if (!active) return;
        setSettings(value);
        setRevision(value.revision);
        setDraft(
          appearanceSettingsSchema.parse(
            value.appearance || { site: {}, company: {} },
          ),
        );
      })
      .catch((error) => active && notify((error as Error).message));
    return () => {
      active = false;
    };
  }, [repository, tenantId]);
  const layer = draft[level];
  const inherited =
    level === "company"
      ? resolveModuleTheme({ site: draft.site })
      : defaultModuleTheme;
  const effective = resolveModuleTheme(draft);
  const levelTheme = resolveModuleTheme(
    level === "site" ? { site: draft.site } : draft,
  );
  const set = (key: keyof ModuleTheme, value: unknown) => {
    setDraft((current) => ({
      ...current,
      [level]: { ...current[level], [key]: value },
    }));
    setDirty(true);
  };
  const usesInherited = (key: keyof ModuleTheme) => layer[key] === undefined;
  const inheritLabel =
    level === "company" ? "Use site configuration" : "Use default";
  const token = (key: keyof ModuleTheme, label: string, control: ReactNode) => (
    <div className="appearance-token" key={key}>
      <div className="appearance-token-control">
        <Control label={label}>{control}</Control>
          <label className="appearance-inherit">
            <input
              type="checkbox"
              checked={usesInherited(key)}
              onChange={(event) =>
                set(key, event.target.checked ? undefined : inherited[key])
              }
            />
            {inheritLabel}
          </label>
      </div>
    </div>
  );
  const save = async () => {
    if (!settings || busy) return;
    setBusy(true);
    try {
      const saved = await repository.saveTenantSettings(
        tenantId,
        { ...settings, appearance: appearanceSettingsSchema.parse(draft) },
        revision,
      );
      setSettings(saved);
      setRevision(saved.revision);
      setDraft(
        appearanceSettingsSchema.parse(
          saved.appearance || { site: {}, company: {} },
        ),
      );
      setDirty(false);
      onSaved?.(
        appearanceSettingsSchema.parse(
          saved.appearance || { site: {}, company: {} },
        ),
      );
      notify("Appearance saved. Plug-in modules use it from now on.");
    } catch (error) {
      notify((error as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="appearance-settings">
      <div className="page-intro">
        <div>
          <span className="eyebrow">SHARED ACROSS EVERY PROJECT</span>
          <h1>Appearance & styles</h1>
          <p>
            Style the plug-in modules — embedded forms, live proofs and
            authoring. Site settings are the defaults; each company can use them
            or override any part.
          </p>
        </div>
      </div>
      <div
        className="section-tabs"
        role="tablist"
        aria-label="Configuration level"
      >
        {(
          [
            ["company", "Company"],
            ["site", "Site"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            role="tab"
            aria-selected={level === id}
            className={level === id ? "active" : ""}
            disabled={id === "site" && !canEditSite}
            onClick={() => setLevel(id)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="appearance-layout">
        <section className="panel padded" aria-label={`${level} styles`}>
          <h2>{level === "site" ? "Site defaults" : "Company styles"}</h2>
          <p className="muted">
            {level === "company"
              ? "Everything starts from the site configuration. Change a setting to customize it, or tick “Use site configuration” to inherit it again."
              : "Defaults for every company on this site."}
          </p>
          <Control label="Start from a preset">
            <Select
              value={
                presets.find(
                  (preset) =>
                    Object.entries(preset.theme).every(
                      ([key, value]) =>
                        layer[key as keyof ModuleTheme] === value,
                    ) &&
                    Object.values(layer).filter((value) => value !== undefined)
                      .length === Object.keys(preset.theme).length,
                )?.name || ""
              }
              onChange={(event) => {
                const preset = presets.find(
                  (row) => row.name === event.target.value,
                );
                if (!preset) return;
                setDraft({ ...draft, [level]: { ...preset.theme } });
                setDirty(true);
              }}
            >
              <option value="">Choose a preset…</option>
              {presets.map((preset) => (
                <option key={preset.name}>{preset.name}</option>
              ))}
            </Select>
          </Control>
          {token(
            "mode",
            "Default display mode",
            <Select
              value={levelTheme.mode}
              onChange={(event) => set("mode", event.target.value)}
            >
              <option value="system">Match the viewer's system</option>
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </Select>,
          )}
          {colorTokens
            .slice(0, 2)
            .map(([key, label]) =>
              token(
                key,
                label,
                <input
                  type="color"
                  value={String(levelTheme[key])}
                  onChange={(event) => set(key, event.target.value)}
                />,
              ),
            )}
          {token(
            "font",
            "Font",
            <Select
              value={levelTheme.font}
              onChange={(event) => set("font", event.target.value)}
            >
              {[
                "Inter",
                "System",
                "Georgia",
                "Libre Baskerville",
                "Monospace",
              ].map((font) => (
                <option key={font}>{font}</option>
              ))}
            </Select>,
          )}
          <details className="appearance-advanced">
            <summary>Advanced styling</summary>
            {colorTokens
              .slice(2)
              .map(([key, label]) =>
                token(
                  key,
                  label,
                  <input
                    type="color"
                    value={String(levelTheme[key])}
                    onChange={(event) => set(key, event.target.value)}
                  />,
                ),
              )}
            {token(
              "radius",
              `Corner radius (${levelTheme.radius}px)`,
              <input
                type="range"
                min={0}
                max={24}
                value={levelTheme.radius}
                onChange={(event) => set("radius", Number(event.target.value))}
              />,
            )}
            {token(
              "fontSize",
              `Base text size (${levelTheme.fontSize}px)`,
              <input
                type="range"
                min={11}
                max={18}
                value={levelTheme.fontSize}
                onChange={(event) =>
                  set("fontSize", Number(event.target.value))
                }
              />,
            )}
            {token(
              "density",
              "Spacing",
              <Select
                value={levelTheme.density}
                onChange={(event) => set("density", event.target.value)}
              >
                <option value="compact">Compact</option>
                <option value="comfortable">Comfortable</option>
                <option value="spacious">Spacious</option>
              </Select>,
            )}
          </details>
          <div className="button-row">
            <Button
              kind="primary"
              disabled={!dirty || busy || !settings}
              onClick={() => void save()}
            >
              Save appearance
            </Button>
            <Button
              disabled={busy || !Object.keys(layer).length}
              onClick={() => {
                setDraft({ ...draft, [level]: {} });
                setDirty(true);
              }}
            >
              {level === "company"
                ? "Use site configuration for everything"
                : "Reset to defaults"}
            </Button>
          </div>
        </section>
        <section className="appearance-preview" aria-label="Module preview">
          <style>
            {moduleThemeCss(
              level === "site"
                ? resolveModuleTheme({ site: draft.site })
                : effective,
              ".appearance-preview-module",
            )}
          </style>
          <span className="eyebrow">LIVE PREVIEW</span>
          <div className="appearance-preview-module proof-authoring">
            <div className="panel padded">
              <h3>Customize your document</h3>
              <p className="muted">How embedded forms look on your sites.</p>
              <Control label="Event name">
                <input defaultValue="A new season begins" />
              </Control>
              <Control label="Event type">
                <Select defaultValue="launch">
                  <option value="launch">Launch celebration</option>
                </Select>
              </Control>
              <div className="button-row">
                <button type="button" className="button primary">
                  Generate document
                </button>
                <button type="button" className="button">
                  Save proof
                </button>
              </div>
              <a href="#preview" onClick={(event) => event.preventDefault()}>
                View saved proofs
              </a>
            </div>
          </div>
          <details>
            <summary>Use in your own pages</summary>
            <p className="muted">
              Embedded modules read these styles from the workspace. To apply
              them to a custom container, add this CSS:
            </p>
            <pre className="appearance-css">
              {moduleThemeCss(effective, "proof-form, proof-authoring")}
            </pre>
          </details>
        </section>
      </div>
    </div>
  );
}
